import React from 'react';
import './styles/layouts/App.css';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme/theme';
import { AuthProvider } from './hooks/useAuth';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import ProtectedRoute from './components/routes/ProtectedRoute';
import MainLayout from './layouts/MainLayout';

// Pages
import {
    LandingPage,
    Login,
    Register,
    Profile,
    VerifyEmail,
    VerificationPending,
    ResetPasswordRequest,
    ResetPassword
} from './pages';

import PublicQuizzes from './pages/public/PublicQuizzes';
import Quiz from './pages/student/Quiz';
import QuizzesList from './pages/QuizzesList';

// Admin Pages
import AdminDashboard from './pages/admin/Dashboard';
import ContentCreators from './pages/admin/ContentCreators';
import SystemSettings from './pages/admin/SystemSettings';

// Content Creator Pages
import ContentCreatorDashboard from './pages/contentCreator/Dashboard';
import ThemeManagement from './pages/contentCreator/ThemeManagement';
import QuestionPapers from './pages/contentCreator/QuestionPapers';
import Questions from './pages/contentCreator/Questions';

// Student Pages
import StudentDashboard from './pages/student/Dashboard';
import AvailableQuizzes from './pages/student/AvailableQuizzes';
import QuizAttempts from './pages/student/QuizAttempts';

const App: React.FC = () => {
    return (
        <I18nextProvider i18n={i18n}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <AuthProvider>
                <Router>
                    <Routes>
                        {/* Public Routes */}
                        <Route path="/" element={<LandingPage />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/verify-email/:token" element={<VerifyEmail />} />
                        <Route path="/verification-pending" element={<VerificationPending />} />
                        <Route path="/reset-password-request" element={<ResetPasswordRequest />} />
                        <Route path="/reset-password/:token" element={<ResetPassword />} />
                        <Route path="/quizzes" element={<MainLayout><QuizzesList /></MainLayout>} />
                        <Route
                            path="/public-quizzes"
                            element={
                                <MainLayout>
                                    <PublicQuizzes />
                                </MainLayout>
                            }
                        />
                        <Route
                            path="/quiz/:attemptId"
                            element={
                                <MainLayout>
                                    <Quiz />
                                </MainLayout>
                            }
                        />

                        {/* Protected Routes */}
                        <Route
                            path="/profile"
                            element={
                                <ProtectedRoute>
                                    <MainLayout>
                                        <Profile />
                                    </MainLayout>
                                </ProtectedRoute>
                            }
                        />

                        {/* Admin Routes */}
                        <Route
                            path="/admin"
                            element={
                                <ProtectedRoute roles={['admin']}>
                                    <MainLayout>
                                        <AdminDashboard />
                                    </MainLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/content-creators"
                            element={
                                <ProtectedRoute roles={['admin']}>
                                    <MainLayout>
                                        <ContentCreators />
                                    </MainLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/system-settings"
                            element={
                                <ProtectedRoute roles={['admin']}>
                                    <MainLayout>
                                        <SystemSettings />
                                    </MainLayout>
                                </ProtectedRoute>
                            }
                        />

                        {/* Content Creator Routes */}
                        <Route
                            path="/content-creator"
                            element={
                                <ProtectedRoute roles={['content_creator', 'admin']}>
                                    <MainLayout>
                                        <ContentCreatorDashboard />
                                    </MainLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/content-creator/themes"
                            element={
                                <ProtectedRoute roles={['content_creator', 'admin']}>
                                    <MainLayout>
                                        <ThemeManagement />
                                    </MainLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/content-creator/papers"
                            element={
                                <ProtectedRoute roles={['content_creator', 'admin']}>
                                    <MainLayout>
                                        <QuestionPapers />
                                    </MainLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/content-creator/papers/:paperId/questions"
                            element={
                                <ProtectedRoute roles={['content_creator', 'admin']}>
                                    <MainLayout>
                                        <Questions />
                                    </MainLayout>
                                </ProtectedRoute>
                            }
                        />

                        {/* Student Routes */}
                        <Route
                            path="/student"
                            element={
                                <ProtectedRoute roles={['student']}>
                                    <MainLayout>
                                        <StudentDashboard />
                                    </MainLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/student/quizzes"
                            element={
                                <ProtectedRoute roles={['student']}>
                                    <MainLayout>
                                        <AvailableQuizzes />
                                    </MainLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/student/attempts"
                            element={
                                <ProtectedRoute roles={['student']}>
                                    <MainLayout>
                                        <QuizAttempts />
                                    </MainLayout>
                                </ProtectedRoute>
                            }
                        />

                        {/* Redirect root to appropriate dashboard based on role */}
                        <Route
                            path="/dashboard"
                            element={
                                <ProtectedRoute>
                                    {({ user }) => {
                                        switch (user.role) {
                                            case 'admin':
                                                return <Navigate to="/admin" replace />;
                                            case 'content_creator':
                                                return <Navigate to="/content-creator" replace />;
                                            default:
                                                return <Navigate to="/student" replace />;
                                        }
                                    }}
                                </ProtectedRoute>
                            }
                        />

                        {/* Catch all route */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </Router>
                </AuthProvider>
            </ThemeProvider>
        </I18nextProvider>
    );
};

export default App;
